import type { JSX } from 'react';
import { useState } from 'react';
import type { TakiConfig } from '@taki/runtime-config';
import { signOutTaki } from '@taki/auth';
import { Button, Card, repliDepuisConfig, useBrandingPort } from '@taki/ui';
import { texteDeRefus, type VerdictEntree } from './refusEntree.js';
import { TakiWordmark } from './TakiWordmark.js';

export interface AccesRefuseScreenProps {
  config: TakiConfig;
  verdict: VerdictEntree;
  /** L'adresse avec laquelle la personne s'est authentifiee, si on la connait. */
  email: string | null;
  /** Rejouer la verification. N'est cable que quand le refus est une panne. */
  onReessayer?: (() => void) | undefined;
}

/**
 * L'ECRAN DE CELUI QUI S'EST AUTHENTIFIE MAIS N'ENTRE PAS.
 *
 * IL EXISTE PARCE QUE LE BOUTON GOOGLE EXISTE. Avant lui, seul quelqu'un a qui
 * on avait donne un mot de passe pouvait arriver jusqu'ici ; maintenant,
 * n'importe quel compte Google atteint cette etape. Le produit doit donc savoir
 * dire non, proprement, a quelqu'un qui n'a rien fait de mal.
 *
 * IL MONTRE L'ADRESSE UTILISEE, et ce n'est pas un detail : le defaut numero un
 * de ce parcours, c'est d'etre entre avec le mauvais compte Google (le perso au
 * lieu du travail). Sans l'adresse a l'ecran, on ne peut pas s'en rendre compte
 * et on appelle le support.
 *
 * IL RESSEMBLE A L'ECRAN DE CONNEXION, volontairement : meme carte, meme logo
 * du client, meme signature. On n'a pas quitte la porte d'entree, on s'y est vu
 * refuser le passage.
 */
export function AccesRefuseScreen({
  config,
  verdict,
  email,
  onReessayer,
}: AccesRefuseScreenProps): JSX.Element {
  const [sortie, setSortie] = useState(false);
  const courant = useBrandingPort()?.courant ?? repliDepuisConfig(config.branding);
  const refus = texteDeRefus(verdict, courant.name);

  return (
    <div className="flex min-h-full w-full items-center justify-center bg-bg p-6 text-ink-900">
      <Card className="w-full max-w-sm p-8 shadow-panel">
        <div className="mb-7 flex flex-col items-center gap-3 text-center">
          {courant.logo ? (
            <img
              src={courant.logo}
              alt={courant.name}
              className="h-9 w-auto max-w-full object-contain"
            />
          ) : (
            <span className="text-lg font-semibold text-ink-900">{courant.name}</span>
          )}
        </div>

        <div className="flex flex-col gap-2 text-center">
          <h1 role="alert" className="text-base font-semibold text-ink-900">
            {refus.titre}
          </h1>
          <p className="text-sm text-ink-500">{refus.detail}</p>
          {email !== null ? (
            <p className="mt-1 break-all font-mono text-xs text-ink-400">{email}</p>
          ) : null}
        </div>

        <div className="mt-7 flex flex-col gap-3">
          {refus.peutReessayer && onReessayer ? (
            <Button
              type="button"
              size="lg"
              className="w-full"
              onClick={() => {
                onReessayer();
              }}
            >
              Reessayer
            </Button>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className="w-full"
            disabled={sortie}
            onClick={() => {
              setSortie(true);
              void signOutTaki().finally(() => {
                setSortie(false);
              });
            }}
          >
            Essayer une autre adresse
          </Button>
        </div>

        <div className="mt-7 flex items-center justify-center gap-2 opacity-50">
          <span className="text-[0.625rem] tracking-[0.04em] text-ink-500">powered by</span>
          <TakiWordmark height={16} className="text-ink-500" />
        </div>
      </Card>
    </div>
  );
}
